import { useMutation, useQueryClient } from "@tanstack/react-query";

import { mutateData } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import { invalidateAfterMutation } from "@/core/api/invalidate-after-mutation";
import { QUERY_KEYS } from "@/core/api/query-keys";
import { logger } from "@/core/utils/func/logger";
import { notificationEndpoints } from "@/features/notification/api/endpoints";
import type { NotificationPreferenceDto } from "@/features/notification/api/dto";
import type { NotificationChannel } from "@/features/shared/api/enums";

interface UpdatePreferenceArgs {
  partyId: string;
  channel: NotificationChannel;
  isEnabled: boolean;
}

/**
 * Active/désactive un canal pour une identité
 * (`PUT /NotificationPreferences/party/{partyId}/{channel}?isEnabled=...`).
 * Attention : `isEnabled` est un booléen NON NULLABLE en query — l'omettre
 * équivaut à désactiver le canal (guide §6.7).
 */
export const useUpdateNotificationPreference = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["notification", "preferences", "update"],
    mutationFn: ({ partyId, channel, isEnabled }: UpdatePreferenceArgs) =>
      mutateData({
        url: `${notificationEndpoints.preferences.updateChannel(
          partyId,
          channel
        )}?isEnabled=${isEnabled}`,
        method: "PUT",
      }) as Promise<NotificationPreferenceDto>,
    onSuccess: (_data, variables) => {
      return invalidateAfterMutation(queryClient, [
        QUERY_KEYS.NOTIFICATION_PREFERENCES(variables.partyId),
      ]);
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useUpdateNotificationPreference failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "notification" }
      );
    },
  });
};
